import { Component, inject } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { OrgListViewMode, OrgListViewService } from './org-list-view.service';

/**
 * The Cards / List switch in the header of the org dashboard's list dialogs (ISS-023). The
 * dialog puts `ng-list-view` on its content when the mode is `list`; the compact row layout
 * itself lives in global-styles.scss so every list dialog reads the same.
 */
@Component({
    selector: 'shared-org-view-toggle',
    imports: [AsyncPipe, MatIconModule, MatTooltipModule],
    template: `
        @let mode = (view.mode$ | async) ?? 'cards';
        <div class="view-toggle" role="group" aria-label="Show as">
            <button
                type="button"
                class="view-toggle-btn"
                aria-label="Cards"
                matTooltip="Cards"
                [attr.aria-pressed]="mode === 'cards'"
                [class.selected]="mode === 'cards'"
                (click)="choose('cards')"
            >
                <mat-icon>grid_view</mat-icon>
            </button>
            <button
                type="button"
                class="view-toggle-btn"
                aria-label="List"
                matTooltip="List"
                [attr.aria-pressed]="mode === 'list'"
                [class.selected]="mode === 'list'"
                (click)="choose('list')"
            >
                <mat-icon>view_list</mat-icon>
            </button>
        </div>
    `,
    styles: [
        `
            :host {
                display: inline-flex;
                flex: 0 0 auto;
                margin-left: auto;
            }
            .view-toggle {
                display: inline-flex;
                height: 32px;
                padding: 2px;
                border-radius: 999px;
                background: var(--ng-em-neutral, #f4f0ec);
            }
            .view-toggle-btn {
                width: 36px;
                height: 28px;
                border: 0;
                border-radius: 999px;
                padding: 0;
                display: inline-flex;
                align-items: center;
                justify-content: center;
                background: transparent;
                color: var(--ng-em-on-neutral, #51443a);
                cursor: pointer;
            }
            .view-toggle-btn .mat-icon {
                font-size: 18px;
                width: 18px;
                height: 18px;
            }
            .view-toggle-btn.selected {
                background: var(--ng-em-tonal, #fdeadd);
                color: var(--ng-em-on-tonal, #8a4500);
                box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
            }
            .view-toggle-btn:focus-visible {
                outline: 2px solid var(--ng-em-primary, #ef6c00);
                outline-offset: 1px;
            }
            /* Phones: the dialog header also holds the title, the explorer and Close (360px). */
            @media (max-width: 767.98px) {
                .view-toggle-btn {
                    width: 28px;
                }
                .view-toggle-btn .mat-icon {
                    font-size: 16px;
                    width: 16px;
                    height: 16px;
                }
            }
            :host-context(.dark-theme) .view-toggle {
                background: rgba(255, 255, 255, 0.08);
            }
            :host-context(.dark-theme) .view-toggle-btn {
                color: rgba(255, 255, 255, 0.7);
            }
        `,
    ],
})
export class OrgViewToggleComponent {
    public readonly view = inject(OrgListViewService);

    public choose(mode: OrgListViewMode): void {
        this.view.set(mode);
    }
}
